import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Trans } from '@lingui/react'
import { useApolloClient } from '@apollo/client'
import { addHours, parseISO } from 'date-fns'

import Timer from './Timer'
import DeleteTimerModalWithState from './DeleteTimerModalWithState'
import PrimaryButton from '../util/PrimaryButton'
import Tooltip from '../util/Tooltip'
import useModalState from '../hooks/useModalState'
import useNotifications from '../hooks/useNotifications'
import useTracking from '../hooks/useTracking'
import { useActivitiesUpdaterContext } from '../providers/ActivitiesUpdaterProvider'
import CreateActivityModalWithState from '../pages/activities-create/CreateActivityModalWithState'
import StartActivityModalWithState from '../pages/activities-create/activity-start/StartActivityModalWithState'
import cancelTimer from '../../api/cancelTimer'
import getActiveTimers from '../../api/activeTimersData'
import startActivity from '../../api/startActivity'
import { getPermissionsReasonMessage } from '../../util/entities'
import pauseTimer from '../../api/pauseTimer'
import {
    getUtcDateTime,
    formatTimeWithSecond,
    formatTime,
    calculateEndDate,
    addSecondsToTime
} from '../../util/dates'

const date = new Date()
const  getSecondsDifference = (nowTimeStr, startTimeStr) => {
    const [nowHours, nowMinutes, nowSeconds] = nowTimeStr.split(':').map(Number)
    const [startHours, startMinutes, startSeconds] = startTimeStr.split(':').map(Number)
  
    const nowInSeconds = nowHours * 3600 + nowMinutes * 60 + nowSeconds
    const startInSeconds = startHours * 3600 + startMinutes * 60 + startSeconds
  
    return nowInSeconds - startInSeconds
  }

const TimerWithState = ({ disabled }) => {
    const apolloClient = useApolloClient()
    const { trackError } = useTracking()
    const { dispatchError, dispatchSuccess } = useNotifications()
    const startActivityModalState = useModalState()
    const createActivityModalState = useModalState()
    const deleteTimerModalState = useModalState()
    const [timer, setTimer] = useState(null)
    const [isActionLoading, setIsActionLoading] = useState(false)
    const [seconds, setSeconds] = useState(0)
    const [isDayChanged, setIsDayChanged] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const { activitiesQueryUpdater } = useActivitiesUpdaterContext()

    useEffect(() => {
        if (!disabled) handlegetActiveTimer()
    }, [disabled])

    useEffect(() => {
        let interval

        if (isActive) {
            interval = setInterval(() => {
                setSeconds((prevSeconds) => prevSeconds + 1)
            }, 1000)
        }

        return () => clearInterval(interval)
    }, [isActive])

    useEffect(() => {
        if (timer && timer.status === 'running' && seconds >= 28800) {
            setIsActive(false)
        }
    }, [seconds])

    const calculeDurationInSecond = (
        status,
        earliestStartDateTime,
        latestStartDateTime,
        elapsedDurationInSeconds,
    ) => {
        const durationInSecond = { isDayChanged: false }
        const now = formatTimeWithSecond(Date.now())
        const earliestTimerStartDate = formatTimeWithSecond(
            new Date(earliestStartDateTime),
        )

        if (status === 'paused' || status === 'expired-duration-exceeded') {
            durationInSecond.timeDurationInSecond = elapsedDurationInSeconds
        }

        if (status === 'running') {
            const latestTimerStartDate = formatTimeWithSecond(
                new Date(latestStartDateTime),
            )
            const difFromStartToNow =  getSecondsDifference(
                now,
                latestTimerStartDate,
            )

            if (elapsedDurationInSeconds === 0) {
                durationInSecond.timeDurationInSecond =
                 getSecondsDifference(
                    now,
                    earliestTimerStartDate,
                )
            }
            if (difFromStartToNow > 0) {
                durationInSecond.timeDurationInSecond =
                    difFromStartToNow + elapsedDurationInSeconds
                durationInSecond.isDayChanged = false
            } else {
                const earliesttimerStartDate = formatTimeWithSecond(
                    new Date(latestStartDateTime),
                )

                const difFromMidnightToNow =
                getSecondsDifference(
                    '23:59:59',
                    earliestTimerStartDate,
                )
                durationInSecond.timeDurationInSecond =
                    difFromMidnightToNow + elapsedDurationInSeconds
                durationInSecond.isDayChanged = true
            }
        }

        return durationInSecond
    }

    const noDataText = () =>
        disabled ? (
            <Tooltip
                position="bottom"
                label={getPermissionsReasonMessage('subscriptionplan')}
            >
                <DisabledTimerBtn isSmall disabled iconName="starTimer">
                    <div className='text-base sm:text-xs md:text-sm'><Trans id="Start timer" /></div>
                </DisabledTimerBtn>
            </Tooltip>
        ) : (
            <SubmitButton
                isSmall
                iconName="starTimer"
                onClick={() => startActivityModalState.open()}
                // isLoading={isSubmitting}
                disabled={startActivityModalState.isOpen}
            >
                <div className='text-base sm:text-xs md:text-sm'><Trans id="Start timer" /></div>
            </SubmitButton>
        )

    const handlegetActiveTimer = async () => {
        const result = await getActiveTimers(apolloClient, {})
        const {
            activeTimersPaginated: { nodes },
        } = result

        if (nodes.length === 0) {
            setTimer(null)
            return
        }

        const timer = nodes[0]
        const {
            earliestStartDateTime,
            latestStartDateTime,
            elapsedDurationInSeconds,
            status,
        } = timer
        const durationInSecond = calculeDurationInSecond(
            status,
            earliestStartDateTime,
            latestStartDateTime,
            elapsedDurationInSeconds,
        )
        const now = formatTimeWithSecond(Date.now())
        const earliestTimerStartDate = formatTimeWithSecond(
            new Date(earliestStartDateTime),
        )
        const { isDayChanged, timeDurationInSecond } = durationInSecond

        const differenceInSeconds = getSecondsDifference(
            now,
            earliestTimerStartDate,
        )
        if(status === "running") {
            if(differenceInSeconds < 60 && elapsedDurationInSeconds === 0) {
                setSeconds(differenceInSeconds)
                setIsActive(true)
                setIsDayChanged(false)
            } else if (timeDurationInSecond >= 28800 ) {
                    setSeconds(28800)
                    setIsActive(false)
            } else {
                    setSeconds(differenceInSeconds)
                    setIsActive(status === 'running' && !isDayChanged)
                    setIsDayChanged(status === 'paused' ? false : isDayChanged)
            }
        } 
        if(status === 'paused' || status === 'expired-duration-exceeded') {
            setSeconds(elapsedDurationInSeconds)
            setIsActive(false)
            setIsDayChanged(false)
        }
        
        setTimer(timer)
    }

    const pauseActivity = async () => {
        const now = formatTimeWithSecond(Date.now())
        const input = {
            timerId: timer?.id,
            dateTime: getUtcDateTime(date, now),
        }
        setIsActionLoading(true)
        try {
            await pauseTimer(apolloClient, { ...input }).then((data) => {
                const { timer } = data
                setTimer(timer)
                setIsActive(false)
            })
            dispatchSuccess({
                content: <Trans id="You successfully paused the Timer!" />,
            })
        } catch (e) {
            trackError(e)
            dispatchError({
                title: <Trans id="Something went wrong!" />,
                content: e.message,
            })
        }
        setIsActionLoading(false)
    }

    // const activity = {}
    const createActivity = () => {
        const {
            status,
            project,
            description,
            id,
            latestStartDateTime,
            earliestStartDateTime,
            elapsedDurationInSeconds,
        } = timer
        const now = formatTime(Date.now())
        const timerStartDate = formatTime(new Date(timer.earliestStartDateTime))
        let timerEndtDate = null
        
        if (status === 'paused' || status === 'expired-duration-exceeded') {
            timerEndtDate = addSecondsToTime(timerStartDate, elapsedDurationInSeconds) 
        } else {
            setIsActive(false)
            if (elapsedDurationInSeconds === 0 && status === "running") {
                const durationInSecond = calculeDurationInSecond(
                    status,
                    earliestStartDateTime,
                    latestStartDateTime,
                    elapsedDurationInSeconds,
                )
                const { timeDurationInSecond } = durationInSecond
                 
                if (timeDurationInSecond >= 28800) {
                    // Parse the ISO date string into a Date object
                    const startDate = parseISO(earliestStartDateTime)
                    // Add 8 hours
                    timerEndtDate = addHours(startDate, 8).toISOString()
                } else {
                    timerEndtDate = addSecondsToTime(timerStartDate, seconds) 
                } 
            }
            if (elapsedDurationInSeconds !== 0) {
                const latestTimerStartDate = formatTimeWithSecond(
                    new Date(latestStartDateTime),
                )
                const now = formatTimeWithSecond(Date.now())
                const differenceInSeconds = getSecondsDifference(
                    now,
                    latestTimerStartDate,
                )

                const newElapseDuration =
                differenceInSeconds + elapsedDurationInSeconds
                const latestStart = calculateEndDate(
                    newElapseDuration,
                    earliestStartDateTime,
                    )
                    timerEndtDate = formatTime(new Date(latestStart))
                }
            }

        createActivityModalState.open({
            start: timerStartDate,
            end: isDayChanged ? '23:59' : timerEndtDate,
            project,
            description,
            type: {
                id: 11,
                slug: 'timer',
                translation: 'timer',
                __typename: 'ActivityType',
            },
            timerId: id,
        })
    }

    const startPausedActivity = async () => {
        const { project } = timer
        const now = formatTimeWithSecond(Date.now())
        const input = {
            startDateTime: getUtcDateTime(date, now),
            projectId: project ? project.id : null,
            description: timer.description,
        }
        setIsActionLoading(true)
        try {
            await startActivity(apolloClient, {
                timerId: timer.id,
                input: { ...input },
            }).then((data) => {
                const { timer } = data
                setTimer(timer)
                setIsActive(true)
            })
            dispatchSuccess({
                content: <Trans id="You successfully started the Timer!" />,
            })
        } catch (e) {
            trackError(e)
            dispatchError({
                title: <Trans id="Something went wrong!" />,
                content: e.message,
            })
        }
        setIsActionLoading(false)
    }

    const handlecancelTimer = () => {
        deleteTimerModalState.open({
            customField: timer,
        })
    }

    const handleCloseCreateActivityModal = () => {
        if (timer.status === 'running' && seconds < 28800 && !isDayChanged)
            setIsActive(true)
        createActivityModalState.close()
    }

    return (
        <>
            {!timer ? (
                noDataText()
            ) : (
                <Timer
                    isDayChanged={isDayChanged}
                    seconds={seconds}
                    timer={timer}
                    isActive={isActive}
                    pauseTimer={pauseActivity}
                    createActivity={createActivity}
                    startActivity={startPausedActivity}
                    cancelTimer={handlecancelTimer}
                    isActionLoading={isActionLoading}
                />
            )}
            {startActivityModalState.isOpen && (
                <StartActivityModalWithState
                    onClose={() => startActivityModalState.close()}
                    setTimer={setTimer}
                    setIsActive={setIsActive}
                    setSeconds={setSeconds}
                    {...startActivityModalState.initialState}
                />
            )}
            {createActivityModalState.isOpen && (
                <CreateActivityModalWithState
                    onClose={() => handleCloseCreateActivityModal()}
                    onComplete={async (path, data) => {
                        try {
                            await activitiesQueryUpdater.add(path, data)
                            await setTimer(null)
                            await setSeconds(0)
                            setIsDayChanged(false)
                        } catch (e) {
                            trackError(e)
                            dispatchError({
                                title: <Trans id="Something went wrong!" />,
                                content: e.message,
                            })
                        }
                    }}
                    {...createActivityModalState.initialState}
                />
            )}
            {deleteTimerModalState.isOpen && (
                <DeleteTimerModalWithState
                    onClose={() => deleteTimerModalState.close()}
                    onSubmit={async ({ id }) => {
                        try {
                            await cancelTimer(apolloClient, id)
                            await handlegetActiveTimer()
                            await setTimer(null)
                            await setSeconds(0)
                            await setIsDayChanged(false)
                            document.title = `Lexor`
                        } catch (e) {
                            trackError(e)
                            dispatchError({
                                title: <Trans id="Something went wrong!" />,
                                content: e.message,
                            })
                        }
                    }}
                    {...deleteTimerModalState.initialState}
                />
            )}
        </>
    )
}

const SubmitButton = styled(PrimaryButton)(
    () => `
    border-top-left-radius:24px;
    border-bottom-left-radius:24px;
    border-top-right-radius:24px;
    border-bottom-right-radius:24px;
`,
)

const DisabledTimerBtn = styled(PrimaryButton)(
    () => `
    border-top-left-radius:24px;
    border-bottom-left-radius:24px;
    border-top-right-radius:24px;
    border-bottom-right-radius:24px;
    color:grey;
    border-color:grey;
    background-color:#FFFF;
    :disabled {
        cursor: not-allowed;
        border-color:grey;
        background-color:#FFFF;
    }
    :hover{
        ti
    }
`,
)

export default TimerWithState
